import { useQueryClient } from "@tanstack/react-query";
import {
  type FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { servicesApi } from "@/api/client";
import { queryKeys } from "@/hooks/queryKeys";
import type { CreateServiceRequest } from "@/types/api";

// Submission state + handler for the new-service form. On success, refreshes
// the services cache and redirects to the created service's detail page.
// `afterCreate` runs once the service exists (e.g. making a new LLM the
// default); its failure is reported but doesn't undo or block the create.
export function useCreateService(
  buildRequest: () => CreateServiceRequest,
  afterCreate?: (name: string) => Promise<void>,
) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // The success redirect is delayed so the confirmation is visible; cancel it
  // if the page unmounts first (e.g. the user navigates away in the window).
  const redirectTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(redirectTimer.current), []);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError(null);
      setLoading(true);

      try {
        const service = buildRequest();
        await servicesApi.create(service);
        if (afterCreate) {
          try {
            await afterCreate(service.name);
          } catch (err) {
            const reason = err instanceof Error ? `: ${err.message}` : "";
            setError(`Service created, but the follow-up step failed${reason}`);
          }
        }
        // Refresh the services cache so the new service shows immediately on
        // the detail/list pages (servicesApi writes outside React Query).
        queryClient.invalidateQueries({ queryKey: queryKeys.servicesRoot });
        setSuccess(true);
        redirectTimer.current = setTimeout(() => {
          navigate(`/services/${encodeURIComponent(service.name)}`);
        }, 1000);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to create service";
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [buildRequest, afterCreate, navigate, queryClient],
  );

  // The toast queue takes the error over once shown (useErrorToast), so the
  // same failure can surface again on the next attempt.
  const clearError = useCallback(() => setError(null), []);

  return { loading, error, success, handleSubmit, clearError };
}
